import { useEffect, useMemo, useState } from "react";
import api, { fmtARS, fmtNum, fmtDate } from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { MagnifyingGlass, PencilSimple, Trash, Package, Plus } from "@phosphor-icons/react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import NumberInput from "@/components/ui/NumberInput";

export default function InsumosPage() {
  const [insumos, setInsumos] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ nombre: "", precio_actual: "", unidad: "unidad", iva_pct: 10.5 });
  const [delTarget, setDelTarget] = useState(null);

  const load = async () => {
    const { data } = await api.get("/insumos");
    setInsumos(data);
  };
  useEffect(() => { load(); }, []);

  const openEdit = (p) => {
    setEditing(p);
    setCreating(false);
    setForm({
      nombre: p.nombre,
      precio_actual: p.precio_actual,
      unidad: p.unidad || "unidad",
      iva_pct: p.iva_pct != null ? p.iva_pct : 10.5,
    });
  };

  const openCreate = () => {
    setCreating(true);
    setEditing(null);
    setForm({ nombre: "", precio_actual: "", unidad: "unidad", iva_pct: 10.5 });
  };

  const closeSheet = () => { setEditing(null); setCreating(false); };

  const save = async (e) => {
    e.preventDefault();
    const nombre = (form.nombre || "").trim();
    if (!nombre) {
      toast.error("El nombre es obligatorio");
      return;
    }
    try {
      const payload = {
        nombre,
        precio_actual: parseFloat(form.precio_actual) || 0,
        unidad: form.unidad || "unidad",
        iva_pct: parseFloat(form.iva_pct) || 10.5,
      };
      if (creating) {
        await api.post("/insumos", payload);
        toast.success("Insumo creado");
      } else if (editing) {
        await api.put(`/insumos/${editing.id}`, payload);
        toast.success("Insumo actualizado");
      }
      closeSheet();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const confirmDelete = async () => {
    if (!delTarget) return;
    try {
      await api.delete(`/insumos/${delTarget.id}`);
      toast.success("Insumo eliminado");
      setDelTarget(null);
      load();
    } catch {
      toast.error("Error");
    }
  };

  const filtered = useMemo(() => {
    if (!q.trim()) return insumos;
    const s = q.toLowerCase();
    return insumos.filter(
      (p) => p.nombre.toLowerCase().includes(s) || (p.codigo || "").toLowerCase().includes(s)
    );
  }, [insumos, q]);

  const totalGastado = useMemo(
    () => insumos.reduce((acc, p) => acc + (p.precio_actual || 0) * (p.total_comprado || 0), 0),
    [insumos]
  );

  return (
    <>
      <Topbar
        title="Insumos y Materias Primas"
        subtitle="Catálogo de compras: insumos, servicios y materias primas."
        right={
          <div className="flex items-center gap-2">
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#999]" />
              <Input
                data-testid="insumos-search"
                placeholder="Buscar código o nombre…"
                className="rounded-sm h-9 w-64 pl-8 text-[13px]"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
            <Button
              onClick={openCreate}
              className="rounded-sm h-9 bg-[#6B1F24] hover:bg-[#571519] gap-1.5"
              data-testid="insumos-nuevo-btn"
            >
              <Plus size={14} weight="bold" /> Nuevo insumo
            </Button>
          </div>
        }
      />
      <div className="p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="border border-[#E5E5E5] bg-white p-5 rounded-sm">
            <div className="text-[11px] uppercase tracking-widest text-[#666] font-mono">Total insumos</div>
            <div className="font-mono text-2xl font-semibold mt-2">{insumos.length}</div>
          </div>
          <div className="border border-[#E5E5E5] bg-white p-5 rounded-sm">
            <div className="text-[11px] uppercase tracking-widest text-[#666] font-mono">Unidades compradas</div>
            <div className="font-mono text-2xl font-semibold mt-2">
              {fmtNum(insumos.reduce((a, p) => a + (p.total_comprado || 0), 0), 1)}
            </div>
          </div>
          <div className="border border-[#E5E5E5] bg-white p-5 rounded-sm">
            <div className="text-[11px] uppercase tracking-widest text-[#666] font-mono">Gasto histórico</div>
            <div className="font-mono text-2xl font-semibold mt-2">{fmtARS(totalGastado)}</div>
          </div>
        </div>

        <div className="border border-[#E5E5E5] bg-white rounded-sm overflow-hidden">
          <div className="px-5 py-3 border-b bg-neutral-50 flex items-center gap-2">
            <Package size={16} weight="bold" className="text-[#6B1F24]" />
            <h2 className="font-heading font-semibold text-[15px]">Catálogo de insumos</h2>
            <span className="ml-auto text-[11px] uppercase tracking-widest font-mono text-[#666]">
              {filtered.length} insumos
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px] grid-table">
              <thead className="bg-neutral-50 text-[11px] uppercase font-mono text-[#666]">
                <tr>
                  <th className="px-5 py-2 text-left w-24">Código</th>
                  <th className="px-5 py-2 text-left">Nombre</th>
                  <th className="px-5 py-2 text-right w-32">Precio actual</th>
                  <th className="px-5 py-2 text-right w-16">IVA%</th>
                  <th className="px-5 py-2 text-left w-16">Unidad</th>
                  <th className="px-5 py-2 text-right w-32">Cant. comprada</th>
                  <th className="px-5 py-2 text-right w-16">Veces</th>
                  <th className="px-5 py-2 text-left w-32">Última compra</th>
                  <th className="px-5 py-2 text-left w-32">Prov. último</th>
                  <th className="px-5 py-2 text-right w-24">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id} data-testid={`ins-${p.id}`} className="group hover:bg-neutral-50">
                    <td className="px-5 py-2 font-mono text-[#6B1F24]">{p.codigo}</td>
                    <td className="px-5 py-2 font-medium">{p.nombre}</td>
                    <td className="px-5 py-2 text-right font-mono font-semibold">{fmtARS(p.precio_actual)}</td>
                    <td className="px-5 py-2 text-right font-mono text-[12px] text-[#666]">
                      {p.iva_pct != null ? fmtNum(p.iva_pct, 1) : "—"}%
                    </td>
                    <td className="px-5 py-2 text-[#666] text-[11px] uppercase font-mono">{p.unidad}</td>
                    <td className="px-5 py-2 text-right font-mono">{fmtNum(p.total_comprado, 2)}</td>
                    <td className="px-5 py-2 text-right font-mono">{p.veces_comprado}</td>
                    <td className="px-5 py-2 font-mono text-[12px]">{fmtDate(p.ultima_compra)}</td>
                    <td className="px-5 py-2 text-[12px] text-[#666] truncate max-w-[180px]">{p.proveedor_ultimo || "-"}</td>
                    <td className="px-5 py-2 text-right">
                      <div className="flex justify-end gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                        <button
                          data-testid={`edit-ins-${p.id}`}
                          onClick={() => openEdit(p)}
                          className="p-1.5 hover:bg-neutral-200 rounded-sm text-[#666] hover:text-[#1A1A1A]"
                        >
                          <PencilSimple size={14} />
                        </button>
                        <button
                          data-testid={`del-ins-${p.id}`}
                          onClick={() => setDelTarget(p)}
                          className="p-1.5 hover:bg-red-50 rounded-sm text-[#666] hover:text-red-600"
                        >
                          <Trash size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={10} className="text-center py-8 text-[#999]">
                      Sin insumos. Cargá una compra desde Excel para poblar el catálogo.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Sheet open={!!editing || creating} onOpenChange={(v) => !v && closeSheet()}>
        <SheetContent className="w-full sm:w-[420px] sm:max-w-[420px]">
          <SheetHeader>
            <SheetTitle className="font-heading">
              {creating ? "Nuevo insumo" : "Editar insumo"}
            </SheetTitle>
          </SheetHeader>
          {(editing || creating) && (
            <form onSubmit={save} className="space-y-4 mt-6">
              {!creating && (
                <div className="text-[11px] font-mono text-[#666]">
                  Código: <span className="text-[#6B1F24]">{editing.codigo}</span>
                </div>
              )}
              <div>
                <Label className="text-[12px]">Nombre</Label>
                <Input required className="rounded-sm mt-1" value={form.nombre}
                  data-testid="ins-form-nombre"
                  placeholder="Ej: Bolsa de vacío 20x30"
                  onChange={(e) => setForm({ ...form, nombre: e.target.value })} />
              </div>
              <div>
                <Label className="text-[12px]">Precio actual (con IVA)</Label>
                <NumberInput required step="0.01" className="rounded-sm mt-1 font-mono text-right"
                  data-testid="ins-edit-precio"
                  value={form.precio_actual}
                  onChange={(e) => setForm({ ...form, precio_actual: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[12px]">Alícuota IVA %</Label>
                  <NumberInput step="0.5" className="rounded-sm mt-1 font-mono text-right"
                    data-testid="ins-edit-iva"
                    value={form.iva_pct}
                    onChange={(e) => setForm({ ...form, iva_pct: e.target.value })}
                    placeholder="10.5" />
                </div>
                <div>
                  <Label className="text-[12px]">Unidad</Label>
                  <Input className="rounded-sm mt-1 font-mono" value={form.unidad}
                    data-testid="ins-edit-unidad"
                    onChange={(e) => setForm({ ...form, unidad: e.target.value })} placeholder="unidad / kg / lt" />
                </div>
              </div>
              <Button type="submit" className="w-full rounded-sm bg-[#6B1F24] hover:bg-[#571519]" data-testid="ins-form-save">
                {creating ? "Crear insumo" : "Guardar cambios"}
              </Button>
            </form>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!delTarget} onOpenChange={(v) => !v && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Eliminar insumo</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Eliminar <span className="font-mono text-[#6B1F24]">{delTarget?.codigo}</span> · {delTarget?.nombre}?
              Las compras históricas no se ven afectadas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="rounded-sm bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
